import React, { useState, useMemo } from "react";
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush } from "recharts";


const PROPERTY_TYPES = ["All", "Single Family", "Condo", "Townhouse"];
const PRICE_RANGES = ["All", "<$300K", "$300K–$700K", "$700K–$1.5M", ">$1.5M"];

const RAW_DATA = [
  { month: "Mar \'24", sf: 268000, condo: 182000, townhouse: 224000, volume: 312, allAvg: 224667 },
  { month: "Apr \'24", sf: 272000, condo: 185000, townhouse: 228000, volume: 368, allAvg: 228333 },
  { month: "May \'24", sf: 280000, condo: 190000, townhouse: 235000, volume: 425, allAvg: 235000 },
  { month: "Jun \'24", sf: 288000, condo: 194000, townhouse: 242000, volume: 448, allAvg: 241333 },
  { month: "Jul \'24", sf: 291000, condo: 196000, townhouse: 245000, volume: 418, allAvg: 244000 },
  { month: "Aug \'24", sf: 288000, condo: 194000, townhouse: 242000, volume: 392, allAvg: 241333 },
  { month: "Sep \'24", sf: 282000, condo: 190000, townhouse: 237000, volume: 348, allAvg: 236333 },
  { month: "Oct \'24", sf: 275000, condo: 186000, townhouse: 231000, volume: 318, allAvg: 230667 },
  { month: "Nov \'24", sf: 268000, condo: 182000, townhouse: 225000, volume: 278, allAvg: 225000 },
  { month: "Dec \'24", sf: 262000, condo: 178000, townhouse: 220000, volume: 242, allAvg: 220000 },
  { month: "Jan \'25", sf: 265000, condo: 180000, townhouse: 222000, volume: 268, allAvg: 222333 },
  { month: "Feb \'25", sf: 272000, condo: 184000, townhouse: 228000, volume: 305, allAvg: 228000 },
  { month: "Mar \'25", sf: 284000, condo: 190000, townhouse: 238000, volume: 362, allAvg: 237333 },
];

const formatPrice = (v) => {
  if (v >= 1000000) return `$${(v / 1000000)?.toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000)?.toFixed(0)}K`;
  return `$${v}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl p-3 shadow-lg min-w-[180px]">
      <p className="font-heading font-semibold text-sm text-foreground mb-2">{label}</p>
      {payload?.map((entry) => (
        <div key={entry?.dataKey} className="flex items-center justify-between gap-4 text-xs font-caption mb-1">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry?.color }} />
            <span className="text-muted-foreground">{entry?.name}</span>
          </span>
          <span className="font-semibold text-foreground">
            {entry?.dataKey === "volume" ? entry?.value : formatPrice(entry?.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function PriceHistoryChart() {
  const [selectedType, setSelectedType] = useState("All");
  const [selectedRange, setSelectedRange] = useState("All");

  const chartData = useMemo(() => {
    return RAW_DATA?.map(d => {
      let price;
      if (selectedType === "Single Family") price = d?.sf;
      else if (selectedType === "Condo") price = d?.condo;
      else if (selectedType === "Townhouse") price = d?.townhouse;
      else price = d?.allAvg;
      return { ...d, displayPrice: price };
    });
  }, [selectedType]);

  return (
    <div className="card-base flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold text-base text-foreground">Price History & Sales Volume</h3>
          <p className="text-xs font-caption text-muted-foreground mt-0.5">13-month trend with volume overlay</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {PROPERTY_TYPES?.map(t => (
              <button
                key={t}
                onClick={() => setSelectedType(t)}
                className={`px-2 py-1 rounded-md text-xs font-caption font-medium transition-standard ${
                  selectedType === t
                    ? "bg-card text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="w-full h-64 md:h-80" aria-label="Price history and sales volume combination chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fontFamily: "Inter", fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId="price"
              orientation="left"
              tickFormatter={formatPrice}
              tick={{ fontSize: 11, fontFamily: "Inter", fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <YAxis
              yAxisId="vol"
              orientation="right"
              tick={{ fontSize: 11, fontFamily: "Inter", fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: 11, fontFamily: "Inter", paddingTop: 8 }}
              iconType="circle"
              iconSize={8}
            />
            <Bar
              yAxisId="vol"
              dataKey="volume"
              name="Sales Volume"
              fill="var(--color-muted)"
              radius={[2, 2, 0, 0]}
              opacity={0.7}
            />
            <Line
              yAxisId="price"
              type="monotone"
              dataKey="displayPrice"
              name="Median Price"
              stroke="var(--color-primary)"
              strokeWidth={2.5}
              dot={{ r: 3, fill: "var(--color-primary)" }}
              activeDot={{ r: 5 }}
            />
            <Brush
              dataKey="month"
              height={20}
              stroke="var(--color-border)"
              fill="var(--color-muted)"
              travellerWidth={6}
              startIndex={0}
              endIndex={RAW_DATA?.length - 1}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
        <span className="text-xs font-caption text-muted-foreground">Price Range:</span>
        {PRICE_RANGES?.map(r => (
          <button
            key={r}
            onClick={() => setSelectedRange(r)}
            className={`px-2 py-0.5 rounded-full text-xs font-caption transition-standard ${
              selectedRange === r
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {r}
          </button>
        ))}
      </div>
    </div>
  );
}